import { CLIENT_BUSINESS_CODE, RUNTIME_TRANSPORT_CODE } from "./business-codes";
import { ApiError } from "./http";

const NOTICE_ROOT_ID = "apk-games-llm-error-notice";
const NOTICE_STYLE_ID = "apk-games-llm-error-notice-style";

function llmErrorTitle(code: number) {
  switch (code) {
    case CLIENT_BUSINESS_CODE.RESOURCE_NOT_FOUND: return "当前应用没有可用模型";
    case CLIENT_BUSINESS_CODE.INVALID_REQUEST: return "模型请求参数错误";
    case CLIENT_BUSINESS_CODE.UNAUTHORIZED: return "登录状态已失效";
    case CLIENT_BUSINESS_CODE.TOO_MANY_REQUESTS: return "请求过于频繁";
    case CLIENT_BUSINESS_CODE.INTERNAL_ERROR: return "模型服务内部错误";
    case CLIENT_BUSINESS_CODE.MOON_BALANCE_INSUFFICIENT: return "月亮余额不足";
    case CLIENT_BUSINESS_CODE.MODEL_STREAM_UNSUPPORTED: return "当前模型不支持流式响应";
    case CLIENT_BUSINESS_CODE.MODEL_ACCESS_DENIED: return "当前账号不能使用该模型";
    case CLIENT_BUSINESS_CODE.MODEL_USAGE_EXHAUSTED: return "模型权益次数已用完";
    case CLIENT_BUSINESS_CODE.MODEL_CROWDED: return "模型当前拥挤";
    case CLIENT_BUSINESS_CODE.MODEL_GATEWAY_REQUEST: return "模型请求无法组装";
    case CLIENT_BUSINESS_CODE.MODEL_GATEWAY_TIMEOUT: return "模型生成超时";
    case CLIENT_BUSINESS_CODE.MODEL_GATEWAY_CANCELED: return "模型请求已取消";
    case CLIENT_BUSINESS_CODE.MODEL_GATEWAY_NETWORK: return "无法连接模型线路";
    case CLIENT_BUSINESS_CODE.MODEL_GATEWAY_READ: return "模型响应读取中断";
    case CLIENT_BUSINESS_CODE.MODEL_UPSTREAM_AUTH: return "当前模型线路不可用";
    case CLIENT_BUSINESS_CODE.MODEL_UPSTREAM_NOT_FOUND: return "上游模型或接口不存在";
    case CLIENT_BUSINESS_CODE.MODEL_UPSTREAM_TIMEOUT: return "上游模型处理超时";
    case CLIENT_BUSINESS_CODE.MODEL_UPSTREAM_RATE_LIMIT: return "上游模型请求过多";
    case CLIENT_BUSINESS_CODE.MODEL_UPSTREAM_SERVER: return "上游模型服务异常";
    case CLIENT_BUSINESS_CODE.MODEL_UPSTREAM_REJECTED: return "上游模型拒绝请求";
    case CLIENT_BUSINESS_CODE.MODEL_RESPONSE_TOO_LARGE: return "模型响应内容过大";
    case CLIENT_BUSINESS_CODE.MODEL_INVALID_JSON: return "模型返回格式错误";
    case CLIENT_BUSINESS_CODE.MODEL_INVALID_RESPONSE: return "模型响应字段缺失";
    case CLIENT_BUSINESS_CODE.MODEL_EMPTY_CONTENT: return "模型没有返回正文";
    case CLIENT_BUSINESS_CODE.MODEL_GATEWAY_UNKNOWN: return "模型线路调用失败";
    case CLIENT_BUSINESS_CODE.MODEL_PERSISTENCE_FAILED: return "模型结果保存失败";
    case RUNTIME_TRANSPORT_CODE.REQUEST_CANCELED: return "请求已取消";
    case RUNTIME_TRANSPORT_CODE.NETWORK_UNREACHABLE: return "无法连接游戏服务器";
    case RUNTIME_TRANSPORT_CODE.REQUEST_FAILED: return "请求发送失败";
    case RUNTIME_TRANSPORT_CODE.RESPONSE_READ_FAILED: return "服务器响应读取中断";
    default: return "AI 服务暂不可用";
  }
}

export function describeLlmError(error: unknown) {
  if (error instanceof ApiError) {
    return { code: error.code, title: llmErrorTitle(error.code), message: error.message };
  }
  const message = error instanceof Error ? error.message : String(error ?? "未知错误");
  return { code: RUNTIME_TRANSPORT_CODE.REQUEST_FAILED, title: "AI 调用失败", message };
}

function ensureNoticeStyle() {
  if (document.getElementById(NOTICE_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = NOTICE_STYLE_ID;
  style.textContent = `
    #${NOTICE_ROOT_ID}{position:fixed;left:0;right:0;bottom:calc(74px + env(safe-area-inset-bottom));z-index:2147483000;display:flex;justify-content:center;padding:12px;pointer-events:none;font-family:"PingFang SC","Microsoft YaHei",sans-serif}
    #${NOTICE_ROOT_ID} .apk-llm-error-card{display:grid;grid-template-columns:minmax(0,1fr) 36px;gap:8px;width:min(100%,390px);max-height:118px;overflow:hidden;padding:11px 10px 11px 13px;border:1px solid rgba(225,91,56,.32);border-radius:14px;background:rgba(255,248,243,.98);box-shadow:0 12px 36px rgba(38,19,12,.2);color:#2e211d;pointer-events:auto}
    #${NOTICE_ROOT_ID} .apk-llm-error-copy{display:grid;gap:5px;min-width:0}
    #${NOTICE_ROOT_ID} .apk-llm-error-title{min-width:0;color:#a83e27;font-size:15px;font-weight:850;line-height:1.35}
    #${NOTICE_ROOT_ID} .apk-llm-error-message{display:-webkit-box;margin:0;color:#5d443c;font-size:12px;font-weight:600;line-height:1.4;overflow:hidden;overflow-wrap:anywhere;-webkit-box-orient:vertical;-webkit-line-clamp:2}
    #${NOTICE_ROOT_ID} .apk-llm-error-fallback{color:#897067;font-size:12px;line-height:1.45}
    #${NOTICE_ROOT_ID} button{display:grid;place-items:center;width:36px;height:36px;border:0;border-radius:10px;background:#f7e9e2;color:#6f5147;font-size:20px;cursor:pointer}
    @media (min-width:768px){#${NOTICE_ROOT_ID}{bottom:24px}}
  `;
  document.head.appendChild(style);
}

// 所有游戏共用同一错误提示层，玩法即使采用本地兜底也不能静默吞掉模型失败。
export function showLlmErrorNotice(error: unknown) {
  if (typeof document === "undefined") return;
  ensureNoticeStyle();
  document.getElementById(NOTICE_ROOT_ID)?.remove();

  const detail = describeLlmError(error);
  const root = document.createElement("div");
  root.id = NOTICE_ROOT_ID;
  root.setAttribute("role", "alert");
  root.setAttribute("aria-live", "assertive");

  const card = document.createElement("section");
  card.className = "apk-llm-error-card";
  const copy = document.createElement("div");
  copy.className = "apk-llm-error-copy";
  const title = document.createElement("div");
  title.className = "apk-llm-error-title";
  title.textContent = detail.title;
  const message = document.createElement("p");
  message.className = "apk-llm-error-message";
  message.textContent = detail.message;
  const fallback = document.createElement("span");
  fallback.className = "apk-llm-error-fallback";
  fallback.textContent = "本次模型调用未成功；当前玩法将使用本地内容或保留原状态。";
  copy.append(title, message, fallback);

  const close = document.createElement("button");
  close.type = "button";
  close.setAttribute("aria-label", "关闭 AI 错误提示");
  close.textContent = "×";
  close.addEventListener("click", () => root.remove(), { once: true });
  card.append(copy, close);
  root.appendChild(card);
  document.body.appendChild(root);
  window.setTimeout(() => root.remove(), 8_000);
}
